import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../../utils/apiService";
import MessageHeaderLayer from "./MessageHeaderLayer";
import CodeSamplesPanel from "./CodeSamplesPanel";
import ToggleBar from "../child/ToggleBar";
import "./ApiTestPage.css";

const GroupLayer = () => {
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [data, setData] = useState({
    client_id: null,
    token: null,
    type: "type",
    typedMsg: "",
    fromTemplet: false,
    selectedTemplet: null,
    groupData: null,
    message: "",
  });

  const fetchTemplates = async () => {
    try {
      const response = await apiCall({
        url: `templet/get-templet`,
        method: "POST",
        body: { type: "text" },
      });
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const hideAlert = (sec = 1) => {
    setTimeout(() => {
      setAlertMessage("");
      setSpinner(false);
    }, sec * 1000);
  };

  const fetchGroups = async (clientId) => {
    if (!clientId) {
      setGroups([]);
      return;
    }
    setGroupsLoading(true);
    try {
      const response = await apiCall({
        url: `groups?id=${encodeURIComponent(clientId)}`,
        method: "GET",
      });
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setGroups(list.filter((g) => g?.id));
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroups([]);
      setAlertMessage({
        message: "Could not load groups. Make sure the instance is connected.",
        class: "danger",
      });
      hideAlert(3);
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleHeaderData = (id, apiKey) => {
    setData((prev) => ({
      ...prev,
      client_id: id || null,
      token: apiKey || prev.token || localStorage.getItem("apiKey"),
      groupData: null,
    }));
    if (id) fetchGroups(id);
    else setGroups([]);
  };

  const validated = () => {
    if (!data.client_id) return { isValid: false, messageText: "Instance is required!" };
    if (!data.groupData?.id) return { isValid: false, messageText: "Group is required!" };
    if (data.fromTemplet) {
      if (!data.selectedTemplet && !data.message) {
        return { isValid: false, messageText: "Template is required!" };
      }
    } else if (!data.typedMsg && !data.message) {
      return { isValid: false, messageText: "Message is required!" };
    }
    return { isValid: true, messageText: "" };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, messageText } = validated();
    if (!isValid) {
      setAlertMessage({ message: messageText, class: "danger" });
      hideAlert(2);
      return;
    }

    setSpinner(true);
    try {
      const body = data.fromTemplet
        ? {
            client_id: data.client_id,
            type: "templet",
            groupData: data.groupData,
            selectedTemplet: data.selectedTemplet || { text: data.message },
          }
        : {
            client_id: data.client_id,
            type: "type",
            typedMsg: data.typedMsg || data.message,
            groupData: data.groupData,
          };

      const response = await apiCall({
        url: "send-message/send-to-group",
        method: "POST",
        body,
      });

      setAlertMessage({
        message: response.msg || (response.success ? "Sent" : "Failed"),
        class: response.success ? "success" : "danger",
      });
      if (response.success) {
        setData((prev) => ({
          ...prev,
          typedMsg: "",
          message: "",
          selectedTemplet: null,
        }));
      }
      hideAlert(3);
    } catch (error) {
      setAlertMessage({
        message: error.message || "Failed to send group message",
        class: "danger",
      });
      hideAlert(3);
    }
  };

  const sampleParams = useMemo(
    () => ({
      clientId: data.client_id,
      token: data.token || localStorage.getItem("apiKey"),
      groupId: data.groupData?.id,
      message: data.fromTemplet
        ? data.selectedTemplet?.text || data.message
        : data.typedMsg || data.message,
    }),
    [
      data.client_id,
      data.token,
      data.groupData?.id,
      data.typedMsg,
      data.message,
      data.fromTemplet,
      data.selectedTemplet,
    ]
  );

  useEffect(() => {
    document.title = "Send Group Message";
    fetchTemplates();
  }, []);

  return (
    <div className="api-test-page">
      <div className="card">
        <div className="card-body">
          <div className="api-test-welcome">
            <div>
              <h5>Group message test</h5>
              <p>
                Message a WhatsApp group, optionally tag everyone with{" "}
                <code>@tagall</code>, and copy API samples.
              </p>
            </div>
            <span className="api-test-badge">
              <Icon icon="mdi:api" width={14} />
              POST · Group
            </span>
          </div>

          <MessageHeaderLayer getHeaderData={handleHeaderData} />

          <div className="api-test-grid">
            <form className="api-test-card" onSubmit={handleSubmit}>
              <div className="api-test-card-head">
                <h6>Group message test</h6>
                <span>Live send</span>
              </div>
              <div className="api-test-card-body">
                <div className="api-test-note">
                  <Icon icon="mdi:information-outline" width={18} />
                  <span>
                    Select a connected instance and group first. Include{" "}
                    <code>@tagall</code> to mention all members.
                  </span>
                </div>

                {alertMessage && (
                  <div className={`api-test-alert ${alertMessage.class}`}>{alertMessage.message}</div>
                )}

                <label className="api-test-label" htmlFor="group-select">
                  Group
                </label>
                <select
                  id="group-select"
                  className="form-select"
                  value={data.groupData?.id || ""}
                  disabled={!data.client_id || groupsLoading}
                  onChange={(e) => {
                    const group = groups.find((g) => g.id === e.target.value);
                    setData((prev) => ({
                      ...prev,
                      groupData: group
                        ? { id: group.id, name: group.name || group.subject }
                        : null,
                    }));
                  }}
                >
                  <option value="">
                    {groupsLoading
                      ? "Loading groups…"
                      : data.client_id
                        ? "Select group"
                        : "Select instance first"}
                  </option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name || group.subject || group.id}
                    </option>
                  ))}
                </select>
                <span className="api-test-help">
                  {data.groupData?.id
                    ? `Group ID: ${data.groupData.id}`
                    : "Group JID ends with @g.us"}
                </span>

                <ToggleBar
                  id="group-template-switch"
                  label="Using template?"
                  checked={data.fromTemplet}
                  onChange={(checked) =>
                    setData((prev) => ({
                      ...prev,
                      fromTemplet: checked,
                      selectedTemplet: null,
                    }))
                  }
                />

                {!data.fromTemplet ? (
                  <>
                    <label className="api-test-label" htmlFor="group-message">
                      Message
                    </label>
                    <textarea
                      id="group-message"
                      rows={5}
                      className="form-control"
                      placeholder="Type message here… (optional: include @tagall)"
                      value={data.typedMsg}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          typedMsg: e.target.value,
                          message: e.target.value,
                        }))
                      }
                    />
                  </>
                ) : (
                  <>
                    <label className="api-test-label" htmlFor="group-template-select">
                      Template
                    </label>
                    <select
                      id="group-template-select"
                      className="form-select"
                      defaultValue=""
                      onChange={(e) => {
                        const text = e.target.value;
                        setData((prev) => ({
                          ...prev,
                          message: text,
                          selectedTemplet: { text },
                        }));
                      }}
                    >
                      <option value="" disabled>
                        Select Template
                      </option>
                      {templates.map((template) => {
                        let text = "";
                        try {
                          text = JSON.parse(template.content)?.text || "";
                        } catch {
                          text = template.content || "";
                        }
                        return (
                          <option key={template.id} value={text}>
                            {template.name}
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}

                <div className="api-test-actions">
                  <button type="submit" className="btn api-test-submit" disabled={spinner}>
                    {spinner ? "Sending…" : "Send to Group"}
                  </button>
                </div>
              </div>
            </form>

            <CodeSamplesPanel kind="group" params={sampleParams} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupLayer;
